import React, { useState } from "react";
import { Copy, Loader2, Plus, X } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  MINUTES_IN_DAY,
  WEEKDAY_FULL_LABELS,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  coverageMinutes,
  dayCoverageLabel,
  formatDayRun,
  groupCoverageRuns,
  isDayCovered,
  validateCoverageSchedule,
} from "./selectors";
import { CoverageHours, CoverageSchedule, WeekdayKey, WorkingHours } from "./types";
import TimeField from "./workingHours/TimeField";

const WEEKDAYS: WeekdayKey[] = ["MON", "TUE", "WED", "THU", "FRI"];

function toCoverageSchedule(hours: WorkingHours | null): CoverageSchedule {
  const schedule = {} as CoverageSchedule;
  for (const day of WEEKDAY_KEYS) {
    schedule[day] = {
      allDay: false,
      windows: (hours?.[day] ?? []).map((window) => ({ ...window })),
    };
  }
  return schedule;
}

function toWorkingHours(schedule: CoverageSchedule): WorkingHours {
  const hours: WorkingHours = {};
  for (const day of WEEKDAY_KEYS) {
    if (schedule[day].windows.length > 0) {
      hours[day] = schedule[day].windows;
    }
  }
  return hours;
}

function formatMinutes(minutes: number): string {
  const wrapped = minutes % MINUTES_IN_DAY;
  const hours = String(Math.floor(wrapped / 60)).padStart(2, "0");
  return `${hours}:${String(wrapped % 60).padStart(2, "0")}`;
}

function nextWindow(windows: CoverageHours[]): CoverageHours {
  if (windows.length === 0) {
    return { start: "09:00", end: "17:00" };
  }
  const lastEnd = windows.reduce(
    (latest, window) =>
      Math.max(latest, coverageMinutes(window.end, true) ?? 0),
    0
  );
  if (lastEnd >= MINUTES_IN_DAY) {
    return { start: "", end: "" };
  }
  return { start: formatMinutes(lastEnd), end: "00:00" };
}

function copyWindowsToDays(
  draft: CoverageSchedule,
  source: WeekdayKey,
  days: WeekdayKey[]
): CoverageSchedule {
  const windows = draft[source].windows.map((window) => ({ ...window }));
  const next = { ...draft };
  for (const day of days) {
    next[day] = {
      allDay: false,
      windows: windows.map((window) => ({ ...window })),
    };
  }
  return next;
}

/** "Mon–Fri 9:00 AM – 5:00 PM, Sat 9:00 AM – 12:00 PM"; null when no hours are set. */
export function workingHoursLabel(hours: WorkingHours | null): string | null {
  const runs = groupCoverageRuns(toCoverageSchedule(hours)).filter((run) =>
    isDayCovered(run.coverage)
  );
  if (runs.length === 0) {
    return null;
  }
  return runs
    .map((run) => `${formatDayRun(run.days)} ${dayCoverageLabel(run.coverage)}`)
    .join(", ");
}

interface WorkingHoursDialogProps {
  providerName: string;
  hours: WorkingHours | null;
  saving: boolean;
  /** Resolves true when the server accepted the hours; the dialog closes only then. */
  onSave: (hours: WorkingHours) => Promise<boolean>;
  onClose: () => void;
}

function WorkingHoursDialog({ providerName, hours, saving, onSave, onClose }: WorkingHoursDialogProps) {
  const [draft, setDraft] = useState<CoverageSchedule>(() => toCoverageSchedule(hours));
  const error = validateCoverageSchedule(draft);

  const setWindows = (day: WeekdayKey, windows: CoverageHours[]) =>
    setDraft((current) => ({ ...current, [day]: { allDay: false, windows } }));

  const save = async () => {
    if (error !== null) {
      return;
    }
    if (await onSave(toWorkingHours(draft))) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">{providerName}: working hours</DialogTitle>
          <DialogDescription className="text-xs">
            The assistant only books this provider inside these hours, in the
            practice&apos;s local time. A day with no hours is a day off. End a range
            at 12:00 AM to run it to midnight.
          </DialogDescription>
        </DialogHeader>

        <div
          className="space-y-0 rounded-lg border border-brand-100 bg-brand-50/60 px-3"
          data-testid="working-hours-form"
        >
          {WEEKDAY_KEYS.map((day) => {
            const slug = day.toLowerCase();
            const fullName = WEEKDAY_FULL_LABELS[day];
            const windows = draft[day].windows;
            const setWindow = (index: number, window: CoverageHours) =>
              setWindows(
                day,
                windows.map((existing, i) => (i === index ? window : existing))
              );
            return (
              <div
                key={day}
                className="flex items-start gap-3 border-t border-brand-100/80 py-2 first:border-t-0"
              >
                <span className="w-8 pt-2 text-xs font-semibold tracking-wide text-brand">
                  {WEEKDAY_LABELS[day]}
                </span>
                <div className="min-w-0 flex-1 space-y-1.5">
                  {windows.length === 0 && (
                    <p className="pt-2 text-xs text-gray-500">Day off</p>
                  )}
                  {windows.map((window, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <TimeField
                        value={window.start}
                        onChange={(start) => setWindow(index, { ...window, start })}
                        aria-label={`${fullName} range ${index + 1} start`}
                        data-testid={`hours-${slug}-start-${index}`}
                      />
                      <span className="text-[11px] text-gray-400">to</span>
                      <TimeField
                        value={window.end}
                        onChange={(end) => setWindow(index, { ...window, end })}
                        aria-label={`${fullName} range ${index + 1} end`}
                        data-testid={`hours-${slug}-end-${index}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                        aria-label={`Remove ${fullName} range ${index + 1}`}
                        onClick={() =>
                          setWindows(day, windows.filter((_, i) => i !== index))
                        }
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-brand"
                      data-testid={`hours-${slug}-add`}
                      onClick={() => setWindows(day, [...windows, nextWindow(windows)])}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Add hours
                    </Button>
                    {windows.length > 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-gray-500"
                          aria-label={`Copy ${fullName} to weekdays`}
                          onClick={() =>
                            setDraft((current) => copyWindowsToDays(current, day, WEEKDAYS))
                          }
                        >
                          <Copy className="mr-1 h-3 w-3" />
                          Weekdays
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-gray-500"
                          aria-label={`Copy ${fullName} to every day`}
                          onClick={() =>
                            setDraft((current) => copyWindowsToDays(current, day, WEEKDAY_KEYS))
                          }
                        >
                          <Copy className="mr-1 h-3 w-3" />
                          Every day
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {error !== null && (
          <p className="text-xs text-red-600" data-testid="working-hours-error">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-brand hover:bg-brand-hover text-white"
            onClick={save}
            disabled={saving || error !== null}
            data-testid="working-hours-save"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save hours
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkingHoursDialog;
